import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, CreatorDetail, Post, Tip, User } from '../lib/api';
import { ContentCard } from '../components/ContentCard';
import { TipModal } from '../components/TipModal';
import { motion } from 'framer-motion';

interface CreatorProfileProps {
  user: User | null;
}

export const CreatorProfile: React.FC<CreatorProfileProps> = ({ user }) => {
  const { id } = useParams<{ id: string }>();
  
  const [detail, setDetail] = useState<CreatorDetail | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [tips, setTips] = useState<Tip[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [following, setFollowing] = useState<boolean>(false);
  const [followingLoading, setFollowingLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isTipModalOpen, setIsTipModalOpen] = useState<boolean>(false);

  const fetchProfileData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);

      // 1. Fetch profile detail
      const profileRes = await api.getCreator(id);
      setDetail(profileRes);

      // 2. Fetch all posts and filter by creator
      // Fetch a larger limit to get the creator's posts
      const postsRes = await api.getPosts(1, 50);
      const creatorPosts = postsRes.posts.filter((p) => p.creator_id === id);
      setPosts(creatorPosts);

      // 3. Fetch creator tips
      const tipsRes = await api.getCreatorTips(id);
      setTips(tipsRes.tips);

      // 4. Resolve follow status
      // We can check if the user is in the followers table or if it toggles.
      // Since Hono follow API returns { followed: boolean } when toggled,
      // let's check if there's follow state we can deduce.
      // For MVP, we can assume false or check if there is a local cache.
      // Let's set following to false initially and let toggle handle it.
      
    } catch (err: any) {
      console.error('Failed to load creator profile', err);
      setError(err.message || 'Creator not found.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, [id]);

  const handleFollow = async () => {
    if (!id || followingLoading) return;
    if (!user) {
      alert('Please connect your wallet first.');
      return;
    }
    try {
      setFollowingLoading(true);
      const res = await api.followCreator(id);
      setFollowing(res.followed);
      // Adjust follower count locally
      if (detail) {
        setDetail({
          ...detail,
          stats: {
            ...detail.stats,
            follower_count: detail.stats.follower_count + (res.followed ? 1 : -1)
          }
        });
      }
    } catch (err) {
      console.error('Failed to follow creator', err);
    } finally {
      setFollowingLoading(false);
    }
  };

  const handleTipSuccess = (amount: number) => {
    // Reload tips and profile stats
    api.getCreatorTips(id!).then(res => setTips(res.tips)).catch(console.error);
    if (detail) {
      setDetail({
        ...detail,
        stats: {
          ...detail.stats,
          total_tips: detail.stats.total_tips + amount
        }
      });
    }
  };

  const handlePostDeleted = (deletedId: string) => {
    setPosts(prev => prev.filter(p => p.id !== deletedId));
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        <div className="glass-card shimmer" style={{ height: '220px' }} />
        <div className="explore-grid">
          {Array(3).fill(0).map((_, idx) => (
            <div key={idx} className="glass-card shimmer" style={{ height: '350px' }} />
          ))}
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="glass-card" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
        <h3>Creator Not Found</h3>
        <p className="text-muted" style={{ marginTop: '0.5rem' }}>{error || 'The requested creator profile does not exist.'}</p>
      </div>
    );
  }

  const { creator, stats } = detail;
  const isSelf = user?.id === creator.id;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
      
      {/* Profile Header */}
      <div className="glass-card" style={{ padding: '2rem' }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          textAlign: 'center',
          gap: '1.25rem'
        }}>
          {/* Avatar */}
          <div style={{ 
            width: '100px', 
            height: '100px', 
            borderRadius: '50%', 
            background: 'var(--gradient-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2.5rem',
            fontWeight: 800,
            boxShadow: '0 0 25px rgba(255, 45, 120, 0.25)',
            border: '2px solid rgba(255, 255, 255, 0.1)',
            overflow: 'hidden'
          }}>
            {creator.avatar_key ? (
              <img src={creator.avatar_key} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              (creator.display_name || creator.username || 'CR').slice(0, 2).toUpperCase()
            )}
          </div>

          <div>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 800 }}>{creator.display_name || creator.username || 'Sole Creator'}</h2>
            <p className="text-muted" style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
              @{creator.username || creator.wallet.slice(0, 6)}...{creator.wallet.slice(-6)}
            </p>
          </div>

          <p style={{ maxWidth: '600px', fontSize: '0.95rem' }}>
            {creator.bio || 'Welcome to my exclusive SOLEMATE profile! Subscribe and hold tokens to unlock my feet content.'}
          </p>

          {/* Stats Summary */}
          <div style={{ display: 'flex', gap: '2rem', margin: '0.5rem 0' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 800, fontSize: '1.2rem' }}>{stats.follower_count}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Followers</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 800, fontSize: '1.2rem' }}>{stats.post_count}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Posts</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 800, fontSize: '1.2rem' }}>{stats.total_tips}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>$SOLEM8 Tipped</div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '1rem', width: '100%', maxWidth: '360px' }}>
            {!isSelf && (
              <>
                <button 
                  className={`btn ${following ? 'btn-secondary' : 'btn-outline-gradient'}`}
                  style={{ flex: 1 }}
                  onClick={handleFollow}
                  disabled={followingLoading}
                >
                  {following ? 'Following 👣' : 'Follow Creator'}
                </button>
                <button 
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  onClick={() => {
                    if (!user) {
                      alert('Please connect your wallet to send a tip.');
                      return;
                    }
                    setIsTipModalOpen(true);
                  }}
                >
                  Tip $SOLEM8 🦶💸
                </button>
              </>
            )}
            {isSelf && (
              <button 
                className="btn btn-secondary"
                style={{ width: '100%' }}
                onClick={() => window.location.href = '/dashboard'}
              >
                Go to Dashboard 📈
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content Columns: Posts & Tips Leaderboard */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '2rem', alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <h3 style={{ fontSize: '1.4rem', fontFamily: 'var(--font-heading)' }}>Exclusive Posts 🦶</h3>
          
          {posts.length === 0 ? (
            <div className="glass-card" style={{ textAlign: 'center', padding: '3rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>👣📭</div>
              <h4>No posts published yet</h4>
              <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                Check back later for exclusive content!
              </p>
            </div>
          ) : (
            <div className="explore-grid">
              {posts.map((post) => (
                <ContentCard 
                  key={post.id} 
                  post={post} 
                  currentUserId={user?.id}
                  onDelete={handlePostDeleted}
                />
              ))}
            </div>
          )}
        </div>

        {/* Tip History */}
        {tips.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '1rem' }}>
            <h3 style={{ fontSize: '1.4rem', fontFamily: 'var(--font-heading)' }}>Recent Tippers 🏆</h3>
            <div className="glass-card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {tips.map((tip) => (
                <div 
                  key={tip.id} 
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    borderBottom: '1px solid var(--border-glass)',
                    paddingBottom: '0.75rem'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                      {tip.from_display_name || `Wallet @${tip.from_wallet.slice(0, 4)}...${tip.from_wallet.slice(-4)}`}
                    </div>
                    {tip.message && (
                      <div className="text-secondary" style={{ fontSize: '0.8rem', marginTop: '0.25rem', fontStyle: 'italic' }}>
                        "{tip.message}"
                      </div>
                    )}
                  </div>
                  <div style={{ fontWeight: 800, color: 'var(--accent-primary)', fontSize: '0.95rem' }}>
                    +{tip.amount_tokens} $SOLEM8
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tip Modal */}
      {isTipModalOpen && (
        <TipModal 
          isOpen={isTipModalOpen}
          onClose={() => setIsTipModalOpen(false)}
          creator={creator}
          onTipSuccess={handleTipSuccess}
        />
      )}

    </div>
  );
};
